import { FC, MouseEvent, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { Button, Input, notification, Row, Space, Typography } from "antd";
import type { TextAreaRef } from "antd/es/input/TextArea";
import {
  CheckCircleFilled,
  EditOutlined,
  ExclamationCircleFilled,
  LoadingOutlined,
  SaveFilled,
} from "@ant-design/icons";
import { translate } from "../../../core";
import { PurchaseOrderUpdate } from "../graphql/purchase-order.graphql";
import { PurchaseOrderUpdateInput } from "../type/purchase-order";

// Save on our own once the user has stopped typing for this long.
const AUTO_SAVE_DELAY_MS = 2000;
// How long "Saved" stays next to the Edit button after the editor closes.
const SAVED_HINT_MS = 3000;

type SaveStatus = "idle" | "saving" | "saved" | "error";

type PurchaseOrderSpecialInstructionProps = {
  purchaseOrderId: number;
  value?: string | null;
  // Rendered between the title row and the text, e.g. the "Show" checkbox.
  children?: ReactNode;
  // Called after every successful save.
  onSaved?: () => void;
};

// Keeps the text area focused when a header button is pressed, so the click
// does not first trigger the leave-the-field save.
const keepFocus = (e: MouseEvent) => e.preventDefault();

/**
 * Special Instruction of a non-pending purchase order. Read-only text with an
 * Edit button; while editing, changes save 2 s after the last keystroke and
 * when the field loses focus, and Save saves at once and closes the editor.
 * Only Save closes it: collapsing on blur would move the page under the
 * pointer mid-click. Saves run one after another, so the newest text is
 * always written last.
 */
export const PurchaseOrderSpecialInstruction: FC<PurchaseOrderSpecialInstructionProps> = ({
  purchaseOrderId,
  value,
  children,
  onSaved,
}) => {
  const client = useApolloClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [saved, setSaved] = useState(value ?? "");
  const [status, setStatus] = useState<SaveStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string>();

  const textAreaRef = useRef<TextAreaRef>(null);
  // Mirrors of the state above for timers, queued saves and unload handlers.
  const draftRef = useRef(draft);
  const savedRef = useRef(saved);
  const editingRef = useRef(false);
  const savingRef = useRef(false);
  const mountedRef = useRef(true);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const queueRef = useRef<Promise<boolean>>(Promise.resolve(true));
  const propsRef = useRef({ client, purchaseOrderId, onSaved });
  propsRef.current = { client, purchaseOrderId, onSaved };

  const saveIfChanged = useCallback(async (): Promise<boolean> => {
    const note = draftRef.current;
    if (note === savedRef.current) return true;

    savingRef.current = true;
    setStatus("saving");
    try {
      const { client, purchaseOrderId } = propsRef.current;
      await client.mutate({
        mutation: PurchaseOrderUpdate,
        variables: {
          input: { id: purchaseOrderId, note } as PurchaseOrderUpdateInput,
        },
      });
      savedRef.current = note;
      setSaved(note);
      // Anything typed while the request ran has its own timer pending.
      setStatus(draftRef.current === note ? "saved" : "idle");
      propsRef.current.onSaved?.();
      return true;
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : undefined);
      setStatus("error");
      // The page is gone (the user navigated away): hand the text back so it
      // can be copied, and keep the message until it is closed.
      if (!mountedRef.current)
        notification.error({
          message: translate("special_instruction"),
          description: (
            <>
              {translate("special_instruction_save_failed_message")}
              {note ? (
                <Typography.Paragraph
                  className="mt-2 mb-0"
                  copyable={{ text: note }}
                  ellipsis={{ rows: 3, expandable: true }}
                >
                  {note}
                </Typography.Paragraph>
              ) : null}
            </>
          ),
          duration: 0,
        });
      return false;
    } finally {
      savingRef.current = false;
    }
  }, []);

  // Saves the current text after any save that is already running.
  const flush = useCallback(() => {
    clearTimeout(timerRef.current);
    queueRef.current = queueRef.current.then(saveIfChanged);
    return queueRef.current;
  }, [saveIfChanged]);

  const closeEditor = () => {
    editingRef.current = false;
    setEditing(false);
  };

  const startEditing = () => {
    draftRef.current = savedRef.current;
    setDraft(savedRef.current);
    editingRef.current = true;
    setEditing(true);
    setStatus("idle");
  };

  const handleChange = (next: string) => {
    draftRef.current = next;
    setDraft(next);
    clearTimeout(timerRef.current);
    if (next !== savedRef.current)
      timerRef.current = setTimeout(flush, AUTO_SAVE_DELAY_MS);
    if (!savingRef.current) setStatus("idle");
  };

  const handleSave = async () => {
    const ok = await flush();
    if (ok && draftRef.current === savedRef.current) closeEditor();
  };

  useEffect(() => {
    if (editing) textAreaRef.current?.focus({ cursor: "end" });
  }, [editing]);

  // A refetch of the purchase order brought a value; take it unless editing.
  useEffect(() => {
    if (editingRef.current || savingRef.current) return;
    const next = value ?? "";
    draftRef.current = next;
    savedRef.current = next;
    setDraft(next);
    setSaved(next);
  }, [value]);

  useEffect(() => {
    if (editing || status !== "saved") return;
    const timer = setTimeout(() => setStatus("idle"), SAVED_HINT_MS);
    return () => clearTimeout(timer);
  }, [editing, status]);

  // Closing or reloading the tab: start the save and ask the browser to warn.
  const hasPendingChanges = (editing && draft !== saved) || status === "saving";
  useEffect(() => {
    if (!hasPendingChanges) return;
    const handler = (e: BeforeUnloadEvent) => {
      void flush();
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasPendingChanges, flush]);

  // Leaving the page inside the app (e.g. Back) mid-edit: save what was typed.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (editingRef.current && draftRef.current !== savedRef.current)
        void flush();
      else clearTimeout(timerRef.current);
    };
  }, [flush]);

  const dirty = editing && draft !== saved;

  const statusContent = (() => {
    switch (status) {
      case "saving":
        return (
          <Typography.Text type="secondary">
            <LoadingOutlined /> {translate("saving")}
          </Typography.Text>
        );
      case "saved":
        return (
          <Typography.Text type="success">
            <CheckCircleFilled /> {translate("saved")}
          </Typography.Text>
        );
      case "error":
        return (
          <Space size={4}>
            <Typography.Text type="danger" title={errorMessage}>
              <ExclamationCircleFilled /> {translate("save_failed")}
            </Typography.Text>
            <Button
              type="link"
              size="small"
              className="p-0"
              onMouseDown={keepFocus}
              onClick={() => void flush()}
            >
              {translate("retry")}
            </Button>
          </Space>
        );
      default:
        return null;
    }
  })();

  return (
    <>
      <Row>
        {/* One line on purpose: a status that wrapped would push the page down mid-click. */}
        <Space>
          <h5>{translate("special_instruction")}</h5>
          {editing ? (
            <Button
              type={dirty ? "primary" : "default"}
              className={dirty ? "btn-primary-gradient" : ""}
              icon={<SaveFilled />}
              onMouseDown={keepFocus}
              onClick={handleSave}
            >
              {translate("save")}
            </Button>
          ) : (
            <Button icon={<EditOutlined />} onClick={startEditing}>
              {translate("edit")}
            </Button>
          )}
          <span aria-live="polite">{statusContent}</span>
        </Space>
      </Row>

      {children ? <Row>{children}</Row> : null}

      <Row>
        {editing ? (
          <Input.TextArea
            ref={textAreaRef}
            className="w-100"
            autoSize={{ minRows: 3, maxRows: 10 }}
            value={draft}
            onChange={(e) => handleChange(e.target.value)}
            onBlur={() => void flush()}
          />
        ) : (
          <span className="fs-6 text-muted">{saved}</span>
        )}
      </Row>
    </>
  );
};
